import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Search,
  FileText,
  MessageSquare,
  Star,
  DollarSign,
  CheckCircle2,
  Rocket,
  Sparkles,
  ArrowRight,
  Briefcase,
  Camera,
  Bell,
  Mic,
  Globe,
  Wallet,
  Download
} from 'lucide-react';

const WORKER_TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to ConstructHelp!',
    subtitle: 'Quick tour of your worker tools',
    description: "In about 90 seconds we'll show you the features that win you more work — including the new Voice Translator, your shareable profile, and direct bank payouts.",
    icon: Rocket,
    color: 'from-emerald-500 to-teal-600',
    image: null,
  },
  {
    id: 'translator',
    title: 'NEW · Voice Translator',
    subtitle: 'Hable inglés o español — instant translation',
    description: 'Tap the mic, speak English or Spanish, and hear the translation back. Trade terms stay accurate (varilla ↔ rebar, tablaroca ↔ drywall). Talk to any contractor in your language.',
    icon: Mic,
    color: 'from-emerald-500 to-teal-600',
    tips: [
      'Tap mic, speak, hear translation back',
      'Construction jargon preserved (rebar, scaffold, joist)',
      'Free for every worker',
      'Find it on your dashboard hero banner'
    ],
    action: { label: 'Open Translator', path: '/translator' }
  },
  {
    id: 'find-jobs',
    title: 'Find Jobs That Match You',
    subtitle: 'Browse by trade, location, pay rate',
    description: 'Discover construction jobs in your area. Filter by trade, location, pay rate, and timeline. Save jobs to apply later or apply on the spot.',
    icon: Search,
    color: 'from-blue-500 to-indigo-600',
    tips: [
      'Filter jobs matching your skills',
      'Save interesting jobs to review later',
      'Map view to find jobs near you',
      'Set up alerts for new opportunities'
    ],
    action: { label: 'Browse Jobs', path: '/browse-jobs' }
  },
  {
    id: 'public-profile',
    title: 'NEW · Your Public Profile',
    subtitle: 'Shareable link to win more work',
    description: 'You get a public profile at /pro/your-name with your reviews, portfolio, and a "Hire Me" CTA. Share the link with past clients, on Facebook, or in your bio — rich previews show on every platform.',
    icon: Globe,
    color: 'from-cyan-500 to-blue-600',
    tips: [
      'Reviews + photo portfolio visible to all',
      'Hire Me button drives direct leads',
      'OpenGraph previews on iMessage/Slack/X/FB',
      'Copy your URL from the dashboard banner'
    ],
    action: { label: 'View My Dashboard', path: '/worker-dashboard' }
  },
  {
    id: 'apply-and-message',
    title: 'Apply & Communicate',
    subtitle: 'Personalize, then respond fast',
    description: 'Write a focused application explaining why you fit. Keep all messaging on-platform — it builds trust and creates a record if any dispute comes up later.',
    icon: MessageSquare,
    color: 'from-pink-500 to-rose-600',
    tips: [
      'Personalize each application',
      'Mention availability and timeline',
      'Use voice messages when on the go',
      'Respond to contractors quickly'
    ],
    action: { label: 'Go to Messages', path: '/messages' }
  },
  {
    id: 'get-paid',
    title: 'Wallet + Bank Payouts',
    subtitle: 'Stripe Connect to your bank',
    description: 'Contractors fund escrow before work begins — you\'re guaranteed payment. Complete milestones, money lands in your wallet, then withdraw to your bank account.',
    icon: Wallet,
    color: 'from-green-500 to-emerald-600',
    tips: [
      'Escrow protects every milestone',
      'Connect your bank for direct deposits',
      'Track all payouts in transaction history',
      'No platform fees on payouts'
    ],
    action: { label: 'Open Wallet', path: '/wallet' }
  },
  {
    id: 'grow',
    title: 'Build Your Reputation',
    subtitle: 'Reviews = more work + better pay',
    description: 'Deliver quality, communicate fast, and ask satisfied contractors for reviews. Higher ratings mean more job offers and better rates over time.',
    icon: Star,
    color: 'from-yellow-500 to-orange-600',
    tips: [
      'Quality work on time',
      'Photos of completed jobs power your portfolio',
      'Ask happy contractors for reviews',
      'Verified Pro badge unlocks 2× more views'
    ],
    action: null
  },
  {
    id: 'install-pwa',
    title: 'Install on Your Phone',
    subtitle: 'One tap from your home screen',
    description: 'ConstructHelp installs as an app — faster cold-starts, full-screen, push notifications for new jobs. Look for the "Install" prompt at the bottom-right; on iPhone use Safari Share → Add to Home Screen.',
    icon: Download,
    color: 'from-purple-500 to-violet-600',
    tips: [
      'Push notifications when a job matches',
      'Voice translator available offline-ready',
      'Long-press icon for quick shortcuts',
      'Works on Android, iOS, and desktop'
    ],
    action: null
  },
  {
    id: 'complete',
    title: "You're Ready to Work!",
    subtitle: 'Start finding jobs today',
    description: "You now know how to find jobs, get hired, talk to any crew, and get paid on ConstructHelp. Good luck building your career!",
    icon: CheckCircle2,
    color: 'from-emerald-500 to-teal-600',
    image: null,
  }
];

const WorkerOnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [animating, setAnimating] = useState(false);

  const step = WORKER_TUTORIAL_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WORKER_TUTORIAL_STEPS.length - 1;
  const Icon = step.icon;

  const goToNext = () => {
    if (isLastStep) {
      handleComplete();
      return;
    }
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev + 1);
      setAnimating(false);
    }, 200);
  };

  const goToPrev = () => {
    if (isFirstStep) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrentStep(prev => prev - 1);
      setAnimating(false);
    }, 200);
  };

  const handleComplete = () => {
    // Mark tutorial as completed in localStorage
    localStorage.setItem('constructhelp_worker_onboarding_completed', 'true');
    onComplete?.();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('constructhelp_worker_onboarding_completed', 'true');
    onClose();
  };

  const handleAction = (path) => {
    handleComplete();
    navigate(path);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden transform transition-all duration-300 ${
          animating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        }`}
        data-testid="worker-onboarding-modal"
      >
        {/* Progress Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 dark:bg-gray-700">
          <div 
            className={`h-full bg-gradient-to-r ${step.color} transition-all duration-500`}
            style={{ width: `${((currentStep + 1) / WORKER_TUTORIAL_STEPS.length) * 100}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
          title="Skip tutorial"
          aria-label="Close tour"
          data-testid="skip-tutorial-btn"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Content */}
        <div className="pt-8 pb-6 px-8">
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            {WORKER_TUTORIAL_STEPS.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? 'w-8 bg-gradient-to-r ' + step.color
                    : idx < currentStep
                    ? 'w-2 bg-green-500'
                    : 'w-2 bg-gray-300 dark:bg-gray-600'
                }`}
                data-testid={`step-indicator-${idx}`}
              />
            ))}
          </div>

          {/* Icon */}
          <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
            <Icon className="h-10 w-10 text-white" />
          </div>

          {/* Title & Description */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {step.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-3">
              {step.subtitle}
            </p>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              {step.description}
            </p>
          </div>

          {/* Tips */}
          {step.tips && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-6">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-yellow-500" />
                Pro Tips:
              </p>
              <ul className="space-y-2">
                {step.tips.map((tip, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action Button (for middle steps) */}
          {step.action && (
            <div className="text-center mb-6">
              <Button
                onClick={() => handleAction(step.action.path)}
                className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white px-6`}
                data-testid={`action-btn-${step.id}`}
              >
                {step.action.label}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                Or continue the tutorial first
              </p>
            </div>
          )}

          {/* Welcome Animation */}
          {step.id === 'welcome' && (
            <div className="flex justify-center gap-3 mb-6 flex-wrap">
              {[Mic, Search, Globe, MessageSquare, Wallet, Star].map((ItemIcon, idx) => (
                <div 
                  key={idx}
                  className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center"
                  style={{ animation: `bounce 1s ease-in-out ${idx * 0.1}s infinite` }}
                >
                  <ItemIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
              ))}
            </div>
          )}

          {/* Completion Celebration */}
          {step.id === 'complete' && (
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full px-4 py-2 mb-4">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-semibold">Tutorial Complete!</span>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <Button
                  onClick={() => handleAction('/browse-jobs')}
                  className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:opacity-90 text-white"
                  data-testid="find-jobs-cta-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Find Jobs
                </Button>
                <Button
                  onClick={() => handleAction('/worker-control-panel')}
                  variant="outline"
                  data-testid="edit-profile-cta-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Footer */}
        <div className="px-8 py-4 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={goToPrev}
            disabled={isFirstStep}
            className={isFirstStep ? 'invisible' : ''}
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>

          <Button
            onClick={goToNext}
            className={`bg-gradient-to-r ${step.color} hover:opacity-90 text-white`}
            data-testid="next-step-btn"
          >
            {isLastStep ? (
              <>
                Start Working
                <Rocket className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
};

export default WorkerOnboardingTutorial;
